// FILE: MemoriesSection.logic.ts
// Purpose: Pure helpers behind the Memoria settings panel — form state, slug
//          derivation, request building, sorting and the index-cost estimate.
// Layer: Web UI logic
// Exports: form types and the helpers listed below

import type {
  AdminMemoryCreateRequest,
  AdminMemoryUpdateRequest,
  AgentMemory,
  AgentMemoryScope,
  ProjectId,
  TagId,
  UserId,
} from "@taskin-code/contracts";
import {
  AGENT_MEMORY_BODY_MAX_LENGTH,
  AGENT_MEMORY_HOOK_MAX_LENGTH,
  AGENT_MEMORY_SLUG_MAX_LENGTH,
  AGENT_MEMORY_TITLE_MAX_LENGTH,
} from "@taskin-code/contracts";

export interface MemoryFormState {
  readonly scope: AgentMemoryScope;
  readonly slug: string;
  readonly title: string;
  readonly hook: string;
  readonly triggerHint: string;
  readonly body: string;
  readonly tagIds: ReadonlyArray<TagId>;
}

export const EMPTY_MEMORY_FORM: MemoryFormState = {
  scope: "general",
  slug: "",
  title: "",
  hook: "",
  triggerHint: "",
  body: "",
  tagIds: [],
};

export const memoryToFormState = (memory: AgentMemory): MemoryFormState => ({
  scope: memory.scope,
  slug: memory.slug,
  title: memory.title,
  hook: memory.hook,
  triggerHint: memory.triggerHint ?? "",
  body: memory.body,
  tagIds: memory.tagIds,
});

/**
 * Turn a title into a candidate slug.
 *
 * Accents are stripped rather than mapped, because the server's pattern admits
 * `[a-z0-9-]` only and a slug is a handle an agent types back verbatim — a
 * memory called "Instalación del SDK" has to reach it as `instalacion-del-sdk`
 * or the read fails on a character nobody can see in the index.
 */
export const slugFromTitle = (title: string): string =>
  title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, AGENT_MEMORY_SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * The client-side half of validation, deliberately mirroring the schema in
 * contracts rather than trusting the round trip: these are the four mistakes a
 * form makes constantly, and a 400 that arrives after the dialog closed is a
 * worse way to learn about them.
 */
export const validateMemoryForm = (form: MemoryFormState): string | null => {
  const slug = form.slug.trim();
  if (slug === "") return "Falta el slug.";
  if (!SLUG_PATTERN.test(slug)) {
    return "El slug sólo admite minúsculas, números y guiones (p. ej. instalar-apk-en-emulador).";
  }
  if (slug.length > AGENT_MEMORY_SLUG_MAX_LENGTH) {
    return `El slug no puede pasar de ${AGENT_MEMORY_SLUG_MAX_LENGTH} caracteres.`;
  }
  if (form.title.trim() === "") return "Falta el título.";
  if (form.title.trim().length > AGENT_MEMORY_TITLE_MAX_LENGTH) {
    return `El título no puede pasar de ${AGENT_MEMORY_TITLE_MAX_LENGTH} caracteres.`;
  }
  if (form.hook.trim() === "") return "Falta el gancho: es la única línea que verán los agentes.";
  if (form.hook.trim().length > AGENT_MEMORY_HOOK_MAX_LENGTH) {
    return `El gancho no puede pasar de ${AGENT_MEMORY_HOOK_MAX_LENGTH} caracteres.`;
  }
  if (form.body.trim() === "") return "Falta el contenido.";
  if (form.body.trim().length > AGENT_MEMORY_BODY_MAX_LENGTH) {
    return `El contenido no puede pasar de ${AGENT_MEMORY_BODY_MAX_LENGTH} caracteres.`;
  }
  return null;
};

/** Set equality over tag ids — order is a click artefact, not a value. */
const sameTagSet = (a: ReadonlyArray<TagId>, b: ReadonlyArray<TagId>): boolean => {
  if (a.length !== b.length) return false;
  const seen = new Set(b);
  return a.every((id) => seen.has(id));
};

export const buildMemoryCreateRequest = (
  form: MemoryFormState,
  owner: { readonly projectId: ProjectId | null; readonly userId: UserId | null },
): AdminMemoryCreateRequest => ({
  scope: form.scope,
  projectId: form.scope === "project" ? owner.projectId : null,
  userId: form.scope === "user" ? owner.userId : null,
  slug: form.slug.trim(),
  title: form.title.trim(),
  hook: form.hook.trim(),
  triggerHint: form.triggerHint.trim() === "" ? null : form.triggerHint.trim(),
  body: form.body.trim(),
  tagIds: form.tagIds,
});

/**
 * Only what changed. Scope and slug are absent by construction — they are
 * identity, and the server refuses to patch them.
 */
export const buildMemoryUpdateRequest = (
  form: MemoryFormState,
  original: AgentMemory,
): AdminMemoryUpdateRequest => {
  const patch: {
    title?: string;
    hook?: string;
    triggerHint?: string | null;
    body?: string;
    tagIds?: ReadonlyArray<TagId>;
  } = {};
  const title = form.title.trim();
  const hook = form.hook.trim();
  const body = form.body.trim();
  const triggerHint = form.triggerHint.trim() === "" ? null : form.triggerHint.trim();
  if (title !== original.title) patch.title = title;
  if (hook !== original.hook) patch.hook = hook;
  if (body !== original.body) patch.body = body;
  if (triggerHint !== original.triggerHint) patch.triggerHint = triggerHint;
  // Compared as a set, not as a list: the picker appends in click order, so a
  // person who unticks a tag and re-ticks it would otherwise send a patch that
  // changes nothing but looks like an edit in the audit trail.
  if (!sameTagSet(form.tagIds, original.tagIds)) patch.tagIds = form.tagIds;
  return patch;
};

/**
 * Live first, then archived, and alphabetical inside each group.
 *
 * Status leads the order because the two answer different questions: what is
 * steering agents right now, and what used to. Sorting by date would mix them.
 */
const STATUS_RANK: Record<AgentMemory["status"], number> = {
  proposed: 0,
  approved: 1,
  archived: 2,
};

export const sortMemories = (memories: ReadonlyArray<AgentMemory>): ReadonlyArray<AgentMemory> =>
  memories.toSorted((a, b) => {
    const byStatus = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    return byStatus !== 0 ? byStatus : a.slug.localeCompare(b.slug);
  });

/**
 * A rough token count for the rendered index.
 *
 * Four bytes to the token is the usual English/Spanish approximation and it is
 * good enough for the only decision it informs — whether the index has grown to
 * a size worth pruning. Labelled as approximate in the UI so nobody reconciles
 * it against a bill.
 */
export const approximateTokens = (bytes: number): number => Math.ceil(bytes / 4);
